import { getConfig } from '@recruiting-solutions/rs-shared';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BehaviorSubject, Observable } from 'rxjs';
import { SearchService } from './search.service';

class LocationServiceController {
    MYLOCATIONKEY = 'MyLocation';
    locationSearch: string | undefined;
    currentLocationSource: BehaviorSubject<{ key: string; location: GeolocationCoordinates } | undefined> = new BehaviorSubject<
        { key: string; location: GeolocationCoordinates } | undefined
    >(undefined);
    currentLocation: Observable<{ key: string; location: GeolocationCoordinates } | undefined> = this.currentLocationSource.asObservable();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private knownLocationsMap: any = {};
    private customerId?: string;

    async setLocation(key: string, countryCodes = 'DE', isKnown = false): Promise<{ key: string; location: GeolocationCoordinates } | undefined> {
        if (!key) {
            if (!this.locationSearch) {
                return;
            }
            key = this.locationSearch;
        }
        if (this.knownLocationsMap[key]) {
            if (this.currentLocationSource.value) {
                if (key !== this.currentLocationSource.value.key) {
                    this.currentLocationSource.next(this.knownLocationsMap[key]);
                }
            } else {
                this.currentLocationSource.next(this.knownLocationsMap[key]);
            }
            return this.knownLocationsMap[key] as { key: string; location: GeolocationCoordinates };
        }
        if (isKnown) {
            const knownLocation = await SearchService.getKnownLocation(key);
            if (knownLocation) {
                this.knownLocationsMap[key] = { key: key, location: knownLocation };
                if (this.currentLocationSource.value) {
                    if (key !== this.currentLocationSource.value.key) {
                        this.currentLocationSource.next(this.knownLocationsMap[key]);
                    }
                } else {
                    this.currentLocationSource.next(this.knownLocationsMap[key]);
                }

                return this.knownLocationsMap[key];
            }
        }
        const location = await this.getLocation(key, countryCodes);
        if (location) {
            this.knownLocationsMap[key] = { key: key, location: location };
            if (this.currentLocationSource.value) {
                if (key !== this.currentLocationSource.value.key) {
                    this.currentLocationSource.next(this.knownLocationsMap[key]);
                }
            } else {
                this.currentLocationSource.next(this.knownLocationsMap[key]);
            }
            return this.knownLocationsMap[key];
        }
        return undefined;
    }

    clearLocation() {
        this.locationSearch = '';
        this.currentLocationSource.next(undefined);
    }

    async getLocation(key: string, countryCodes: string = 'DE'): Promise<Location> {
        if (!this.customerId) {
            let config = getConfig();
            this.customerId = config.customerId;
        }
        const fireFunctions = getFunctions();
        let searchFunction = httpsCallable(fireFunctions, 'geoSearch');

        let result = await searchFunction({ searchTerm: key, country: countryCodes });
        return result.data as Location;
    }

    setMyLocation(): Promise<{ key: string; location: GeolocationCoordinates }> {
        return new Promise((res, rej) => {
            if (this.knownLocationsMap[this.MYLOCATIONKEY]) {
                if (this.currentLocationSource.value) {
                    if (this.MYLOCATIONKEY !== this.currentLocationSource.value.key) {
                        this.currentLocationSource.next(this.knownLocationsMap[this.MYLOCATIONKEY]);
                    }
                } else {
                    this.currentLocationSource.next(this.knownLocationsMap[this.MYLOCATIONKEY]);
                }
                res(this.knownLocationsMap[this.MYLOCATIONKEY]);
                return;
            }
            navigator.geolocation.getCurrentPosition(
                async position => {
                    this.knownLocationsMap[this.MYLOCATIONKEY] = { key: this.MYLOCATIONKEY, location: position.coords };
                    if (this.currentLocationSource.value) {
                        if (this.MYLOCATIONKEY !== this.currentLocationSource.value.key) {
                            this.currentLocationSource.next(this.knownLocationsMap[this.MYLOCATIONKEY]);
                        }
                    } else {
                        this.currentLocationSource.next(this.knownLocationsMap[this.MYLOCATIONKEY]);
                    }
                    res(this.knownLocationsMap[this.MYLOCATIONKEY]);
                },
                error => {
                    rej(error);
                },
            );
        });
    }
}

export const locationService = new LocationServiceController();
